import { useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import WaterSystemSummaryTable from './WaterSystemSummaryTable';
import { GET_WATER_SYSTEM_MONITORING_REPORT } from "../../queries/publicQrys/WaterSystemReportQrys";
import { GET_WATER_SYSTEM_SUMMARY } from "../../queries/publicQrys/SystemHeaderViewQrys";
import { convertTimeStampToDate } from "../../utilities/commonFunctions/timeConversionFunctions";
import { InfoModal } from "../reuseableComponents/monitoringReport/InfoModal";
import { ReportSection } from "../reuseableComponents/monitoringReport/ReportSection";
import { ComplianceScheduleTable } from "./ComplianceScheduleTable"
import { GroupedSourceSamplingTable } from "./GroupedSourceSamplingTable";
import './WaterMonitoringReportPrint.css';

const WaterMonitoringPage = ({ isEmbedded = false }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    content: '',
    position: { top: 0, left: 0 }
  });

  const handleOpenModal = (event, title, content) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setModalState({
      isOpen: true,
      title,
      content,
      position: {
        // Correct position calculation including scroll offset
        top: rect.top + window.scrollY + rect.height / 2,
        left: rect.right + window.scrollX + 12
      }
    });
  };

  const handleCloseModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const getSectionHeaders = (sectionData) => {
    let headers = [];
    if (sectionData.showFacilityDetails) {
      headers.push('Facility ID', 'Facility Name');
    } else {
      headers.push('Facility ID', 'Facility Name', 'Analyte Name', 'Sample Required', 'Type', 'Frequency', 'Schedule Begin');
      if (sectionData.showSampleStatus) {
          headers.push('Last Sampled', 'Sample Status');
        } else {
          headers.push('Schedule End');
        }
      headers.push({ title: 'Next Sample Due', subheaders: ['Year(s)', 'Collection Period'] });
    }
    headers.push('Details');
    return headers;
  };

  const getSecondaryHeaders = (sectionData) => {
    if (sectionData.showFacilityDetails) {
      return [
        'Analyte Name', 
        'Samples Required', 
        'Type', 
        'Frequency', 
        'Schedule Begin',
        'Last Sampled', 
        'Sample Status', 
        { title: 'Next Sample Due', subheaders: ['Year(s)', 'Collection Period'] },
        'Details'
      ];
    }
    return [];
  };

  const getSamplePointHeaders = (facility, showSampleStatus) => {
    if (facility.samplePoints && facility.samplePoints.length > 0) {
      let headers = [
        'Sample Point', 
        'Location', 
        'Samples Required',
      ];
      if (showSampleStatus) {
        headers.push('Last Sampled', 'Sample Status');
      }
      headers.push({ title: 'Next Sample Due', subheaders: ['Year(s)', 'Collection Period'] });
      return headers;
    }
    return [];
  }

  const getMonitoringData = (sectionData) => {
    return (sectionData?.schedules ?? []).map(schedule => {
      let cellData = [
        schedule.facility?.facilityCode || '',
        schedule.facility?.name || '',
        schedule.analyte?.name || schedule.analyteGroup?.name || '',
        schedule.requiredSamplesPerPeriod,
        schedule.sampleType,
        schedule.frequency,
        convertTimeStampToDate(schedule.scheduleBeginDate),
      ];

      // Use the showSampleStatus flag to determine if sample fields should be shown
      if (sectionData.showSampleStatus) {
        cellData.push(
          convertTimeStampToDate(schedule.lastSampled),
          schedule.sampleStatus,
          schedule.nextSampleDueYears,
          schedule.nextSampleDuePeriod
        );
      } else {
        cellData.push(
          convertTimeStampToDate(schedule.scheduleEndDate),
          schedule.nextSampleDueYears,
          schedule.nextSampleDuePeriod
        );
      }

      // Add a placeholder for the 'Details' column
      cellData.push(''); 

      return {
        cells: cellData,
      };
    });
  };

  // Monitoring Requirements By Facility
  const getMonitoringRequirementsByFacilityData = (sectionData) => {
    const groupedSchedules = (sectionData?.schedules ?? []).reduce((acc, schedule) => {
      const facilityCode = schedule.facility.facilityCode;
      if (!acc[facilityCode]) {
        acc[facilityCode] = [];
      }
      acc[facilityCode].push(schedule);
      return acc;
    }, {});
    let facilities = [];

    for (const facilityCode in groupedSchedules) {
      if (facilityCode !== null) {
        const schedules = groupedSchedules[facilityCode];
        const facility =  schedules[0].facility;
        let cells = [facility.facilityCode, facility.name];
        let secondaryRows = [];

        // Process schedules for each facility
        schedules.forEach((schedule, idx) => {
          // Do something with each schedule
          let tertiaryRows = [];
          if (schedule.facility.samplePoints && schedule.facility.samplePoints.length > 0) {
            tertiaryRows = schedule.facility.samplePoints.map((sp, spIdx) => ({
              id: `${sp.samplePointId}-${spIdx}`,
              cells: [
                sp.code,
                sp.name,
                sp.requiredSamplesPerPeriod,
                convertTimeStampToDate(sp.lastSampled),
                sp.sampleStatus,
                sp.nextSampleDueYears,
                sp.nextSampleDuePeriod
              ]
            }));
          }
          secondaryRows.push({
            id: schedule.scheduleId,
            cells: [
              schedule?.analyte?.name || schedule?.analyteGroup?.name || '',
              schedule.requiredSamplesPerPeriod,
              schedule.sampleType,
              schedule.frequency,
              convertTimeStampToDate(schedule.scheduleBeginDate),
              convertTimeStampToDate(schedule.lastSampled),
              schedule.sampleStatus,
              schedule.nextSampleDueYears,
              schedule.nextSampleDuePeriod
            ],
            tertiaryGrid: {
              isCollapsible: true,
              headers: getSamplePointHeaders(schedule.facility, true),
              rows: tertiaryRows
            }
          });
        });

        facilities.push({ cells, secondaryRows });
      }
    }

    return facilities;
  };

  const location = useLocation();
  const pwsId = new URLSearchParams(location.search).get('pwsId');
  const [waterSystem, setWaterSystem] = useState(null);
  const [getWaterMonitoringReport, { data: WaterMonitoringReportData }] = useLazyQuery(GET_WATER_SYSTEM_MONITORING_REPORT);
  const [getWaterSystemSummary] = useLazyQuery(GET_WATER_SYSTEM_SUMMARY, {
    onCompleted: (data) => setWaterSystem(data?.getWaterSystemSummary || null)
  });
  const lastFetchedPwsId = useRef(null);

  // This page is driven solely by the pwsId in the URL; it never reads or writes the shared water system selection
  useEffect(() => {
    if (pwsId && pwsId !== lastFetchedPwsId.current) {
      lastFetchedPwsId.current = pwsId;
      getWaterMonitoringReport({ variables: { pwsId } });
      getWaterSystemSummary({ variables: { pwsId } });
    }
  }, [pwsId, getWaterMonitoringReport, getWaterSystemSummary]);

  const reportSectionDataRaw = WaterMonitoringReportData?.getWaterMonitoringReport.sections || [];
  const complianceScheduleDataRaw = WaterMonitoringReportData?.getWaterMonitoringReport?.openComplianceSchedules || [];

  const reportSectionData = reportSectionDataRaw.map(section => {
    const { sectionId, sectionName } = section;
    let sectionData = {};
    let headers = getSectionHeaders(section);
    let secondaryHeaders = section.showFacilityDetails ? getSecondaryHeaders(section) : [];
    let sectionDataArray;

    switch (sectionName) {
      case 'Monitoring Requirements By Facility':
        sectionDataArray = getMonitoringRequirementsByFacilityData(section);
        break;
      default:
        sectionDataArray = getMonitoringData(section);
        break;
    }

    sectionData.sectionTitle = sectionName;
    sectionData.mainGrid = {
      headers: headers,
      rows: Array.isArray(sectionDataArray) && sectionDataArray.map((data, index) => {
        // Find the matching schedule for this row
        let schedule = section.schedules ? section.schedules[index] : null;
        let samplePointGrid = {};
        if (schedule && schedule.facility && schedule.facility.samplePoints && schedule.facility.samplePoints.length > 0) {
          samplePointGrid = {
            isCollapsible: true,
            headers: getSamplePointHeaders(schedule.facility, section.showSampleStatus),
            rows: schedule.facility.samplePoints.map((sp, spIdx) => {
              let cells = [
                sp.code,
                sp.name,
                sp.requiredSamplesPerPeriod,
              ];
              if (section.showSampleStatus) {
                cells.push(
                  convertTimeStampToDate(sp.lastSampled),
                  sp.sampleStatus
                );
              }
              cells.push(
                sp.nextSampleDueYears,
                sp.nextSampleDuePeriod
              );
              return {
                id: `${sp.samplePointId}-${spIdx}`,
                cells: cells
              };
            })
          };
        }
        return {
          id: `${sectionId}-${index}`,
          cells: data.cells || [],
          secondaryGrid: section.showFacilityDetails && secondaryHeaders.length > 0 ? {
            isCollapsible: true,
            headers: secondaryHeaders,
            rows: data.secondaryRows || []
          } : samplePointGrid
        };
      })
    };

    return sectionData;
  });

  return (
    <div className="page-container water-monitoring-page">
      {pwsId && (
        <>
          {!isEmbedded && (
            <>
              <h1 className="water-monitoring-title">Public Water System Water Monitoring Report</h1>
              <WaterSystemSummaryTable selectedWaterSystem={waterSystem} panelLock={true}/>
              <br />
            </>
          )}
          {reportSectionData.map((section, index) => (
              <ReportSection 
                key={index} 
                title={section.sectionTitle} 
                gridData={section.mainGrid} 
                onOpenModal={handleOpenModal}
              />
            ))}
          <GroupedSourceSamplingTable />
          <ComplianceScheduleTable 
            complianceScheduleData={complianceScheduleDataRaw}
          />
          <InfoModal 
            isOpen={modalState.isOpen}
            onClose={handleCloseModal}
            title={modalState.title}
            content={modalState.content}
            position={modalState.position}
          />
        </>
      )}
    </div>
  );
}

export default WaterMonitoringPage;